import * as React from "react";
import { useParams } from "react-router-dom";
import UserPayroll from "../index";
import { useEffect, useState } from "react";
import UserProfile from "../..";
import { connect } from "react-redux";
import { Roles } from "../../../../components/roles";
import {
  UserGrossPayInput,
  UserGrossPayView,
  CompanyGrossPayView,
} from "../../../../models/payroll";
import { AddUserGrossPay, GetUserGrossPaysPerMonth, UpdateUserGrossPay, GetCompanyGrossPays } from "../../../../api/Payroll/grossPay";
import YearMonthPicker from "../../../../components/yearMonthPicker";
import { GetCompanyRates } from "../../../../api/General/currency";
import { CompanyCurrencyRate } from "../../../../models/general";
import { getCompanyRules } from "../../../../api/General/Company";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils";
import CurrencyDropdown from "../../../../components/currencyDropdown";
import UserGrossPayDialog from "./UserGrossPayDialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import AppIcon from "../../../../components/AppIcon";

type Props = Readonly<{
  component?: React.ComponentType<any> | undefined;
  role?: any;
  company?: any;
  userId?: any;
  CompanyCurrencyRate?: any;
}>;


const SalaryConfig = (props: Props) => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const userRole = props.role;
  const [grossPays, setGrossPays] = useState<UserGrossPayView[]>([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [companyGrossPays, setCompanyGrossPays] = useState<CompanyGrossPayView[]>([]);
  const [openGrossPayDialog, setOpenGrossPayDialog] = useState(false);
  const [editGrossPay, setEditGrossPay] = useState<UserGrossPayView | null>({userId: id ?? ""} as UserGrossPayView);
  const [currencyId, setCurrencyId] = useState<any>(0);
  const [currency, setCurrency] = useState<any>(0);
  const [grossPayError, setGrossPayError] = useState<string | null>(null);
  const [companyCurrencyRates, setCompanyCurrencyRates] = useState<CompanyCurrencyRate[]>([]);

  useEffect(() => {
    fetchData();
  }, [id, year, month, currencyId]);
  const handleYearMonthChange = async (date: Date) => {
    const selectedYear = date.getFullYear();
    const selectedMonth = date.getMonth();
    setYear(selectedYear);
    setMonth(selectedMonth);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const grossPaysResult = await GetUserGrossPaysPerMonth(id ?? "", `${year}-${month + 1}-01`, currencyId);
      setGrossPays(grossPaysResult.payload);
      if (Roles.HR.includes(userRole)) {
        const salaryComponents = (await GetCompanyGrossPays()).payload;
        setCompanyGrossPays(salaryComponents);
      }
      if (companyCurrencyRates.length === 0) {
        const companyCurrencyRates_ = await GetCompanyRates();
        const rates: CompanyCurrencyRate[] = [];
        const rules = await getCompanyRules(props.company?.id);
        const baseCurr: CompanyCurrencyRate = {
          id: 0,
          baseCurrency: rules.payload.currency,
          baseCurrencyId: rules.payload.currency.id,
          foreignCurrency: rules.payload.currency,
          foreignCurrencyId: 0,
          company: props.company,
          companyId: props.company?.id,
          rate: 0,
          createdOn: new Date(),
          isArchived: false,
        };
        rates.push(baseCurr);
        const rates_: CompanyCurrencyRate[] = companyCurrencyRates_.payload;
        rates.push(...rates_);
        setCompanyCurrencyRates(rates);
      }
    } catch (ex) {
      console.log("exception", ex);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGrossPayDialog = (grossPay?: UserGrossPayView) => {
    setEditGrossPay(grossPay || ({ userId: id ?? "" } as UserGrossPayView));
    setOpenGrossPayDialog(true);
  };

  const handleCloseGrossPayDialog = () => {
    setOpenGrossPayDialog(false);
    setEditGrossPay(null);
    setGrossPayError(null);
  };

  const handleSaveGrossPay = async (input: UserGrossPayInput) => {
    setLoading(true);
    setGrossPayError(null);
    try {
      if (editGrossPay?.id) {
        await UpdateUserGrossPay(input);
      } else {
        await AddUserGrossPay(input);
      }
      await fetchData();
      handleCloseGrossPayDialog();
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        const messages = error.errors
          .map((err: any) => err.description)
          .filter(Boolean);
        if (messages.length > 0) {
          setGrossPayError(messages.join(". "));
        }
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      <UserProfile profile={!Roles.HR.includes(userRole)}>
        <UserPayroll>
          <div className="flex flex-col gap-4">
              <div className="flex-between">
                <YearMonthPicker
                  date={new Date(year, month)}
                  onChange={handleYearMonthChange}
                  fromMonth={new Date(new Date().getFullYear() - 8, 0)}
                  toMonth={new Date(new Date().getFullYear() + 1, 11)}
                  isRtl={false}
                />
                  <CurrencyDropdown
                    value={currency}
                    options={companyCurrencyRates}
                    onOptionChange={(option: any) => {
                      setCurrency(option);
                      setCurrencyId(option.foreignCurrencyId);
                    }}
                  />
                  <div className="flex-grow" />
                  {Roles.HR.includes(userRole) && (
                    <Button
                      variant="default"
                      onClick={() => handleOpenGrossPayDialog()}
                      disabled={loading}
                      iconName="Plus"
                    >
                      {t("ADD GROSS PAY")}
                    </Button>
                  )}
              </div>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table aria-label="user gross pays table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center">{t("Pay Items")}</TableHead>
                      <TableHead className="text-center">{t("Monthly Pay")}</TableHead>
                      <TableHead className="text-center">{t("Remarks")}</TableHead>
                      <TableHead className="text-center" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center p-4">
                          <svg className="animate-spin h-7 w-7 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && grossPays.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground p-4">
                          {t("No gross pays found for selected period")}
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && grossPays.map((row, index) => (
                      <TableRow key={row.id || index}>
                        <TableCell className="text-center">
                          <strong>{row.companyGrossPay?.name}</strong>
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">
                          {formatNumber(row.amount)}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {row.remarks || t("No Remarks available")}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="icon" aria-label={t("Edit gross pay") as string} onClick={() => handleOpenGrossPayDialog(row)}>
                            <AppIcon name="Pencil" size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            {!loading && grossPays.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4 flex flex-wrap gap-6">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t("totalSalary")}
                  </span>
                  <span className="text-lg font-semibold text-foreground tabular-nums">
                    {formatNumber(grossPays.reduce((sum, gp) => sum + (gp.amount || 0), 0))}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t("wpsSalary")}
                  </span>
                  <span className="text-lg font-semibold text-foreground tabular-nums">
                    {formatNumber(grossPays.filter((gp) => gp.companyGrossPay?.includedInWps === true).reduce((sum, gp) => sum + (gp.amount || 0), 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
        </UserPayroll>
      </UserProfile>
      <UserGrossPayDialog
        open={openGrossPayDialog}
        onClose={handleCloseGrossPayDialog}
        onSave={handleSaveGrossPay}
        companyGrossPays={companyGrossPays}
        editGrossPay={editGrossPay}
        existingGrossPays={grossPays}
        errorMessage={grossPayError}
        defaultMonth={month}
        defaultYear={year}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    role: state.user.role,
    userId: state.user.userId,
    company: state.user.company,
    CompanyCurrencyRate: state.user.CompanyCurrencyRate,
    CompanyCurrencyRateRates: state.user.CompanyCurrencyRateRates,
  };
};

export default connect(mapStateToProps)(SalaryConfig) as any;
